/**
 * Writes a Products-entity record from a Signify product, price and
 * availability response. Consumers: the Signify product modal and the
 * configuration sidebar, both of which hand over the session's overrides.
 *
 * One resolution pass covers every destination except the record's name:
 * `SIGNIFY_RECORD_NAME_DESTINATION` is an ordinary destination in the org's
 * rules, so it carries brand conditions, but it is skipped here and read
 * through `resolveSignifyRecordName`, which takes the first source of the
 * matched rule that yields text rather than the first that yields anything. The
 * two ways that can fail — no rule produced a value, and a rule produced
 * something that is not text — are separate refusals, because only the second
 * names a rule an admin can fix, by the position the settings editor numbers it
 * with. Prices become ordinary paths through
 * `indexSignifyProductPrices`, and `signifyLineItemFieldMappings` layers the
 * fixed maps over the org's rules. A session override is keyed by response
 * path, so it replaces the single destination the surface showed a row for. That
 * destination arrives as `surfacedDestinations`, the very map the surface
 * rendered from `resolveSignifySourceMappings`; this hook never re-derives it,
 * because a second inversion over different inputs disagrees with the rows the
 * operator saw and silently overrides a field they never touched. Every other
 * destination reading that path keeps its configured value, since no row ever
 * offered it. A `null` leaves the destination unwritten rather than falling
 * through to the next source, and a path no rule mentions adds a write.
 * `product.code` is exempt: the duplicate lookup depends on it, so a session
 * cannot repoint it.
 *
 * Accepted limit of SYD-2424: a destination filled by a `constant` source has no
 * response path, so no session override can reach it.
 */
import { useCallback, useContext } from 'react';
import { serverTimestamp } from 'firebase/firestore';
import { ProductAndPriceAndAvailabilityResponse } from '#types/SignifyApi.types';
import { EntityRecord, Fields, AgentType } from '#types/System.types';
import { RecordStatus } from '#types/System.Parameters.types';
import { useAdd } from '../../../../../use/data/useAdd';
import { useSet } from '../../../../../use/data/useSet';
import { useGet } from '../../../../../use/data/useGet';
import { UserContext } from '../../../../../providers/UserContext';
import { FieldTypeContext } from '../../../../../providers/FieldTypeContext';
import {
    indexSignifyProductPrices,
    resolveSignifyFieldValues,
    REQUIRED_PRODUCT_ID_MAPPING,
    REQUIRED_SOURCE_ID_MAPPING,
    SIGNIFY_RECORD_NAME_DESTINATION
} from '#types';
import { useSignifyMappings } from '../../../useSignifyMappings';
import { signifyLineItemFieldMappings } from '../../../signifyLineItemFieldMappings';
import { coerceSignifyFieldValue } from '../../../coerceSignifyFieldValue';
import { useFindLineItemByProductId } from './useFindLineItemByProductId';
import { getNestedValue, resolveSignifyRecordName } from './services';

export type UpsertStatus = 'created' | 'updated' | 'unchanged' | 'duplicate';

/**
 * What one configuration session changed, as the surface that rendered it knows
 * it. Both halves are keyed by response path and must come from the same render:
 * `customMappings` is what the operator chose, `surfacedDestinations` is what
 * each row offered before they chose.
 */
export type SignifySessionMappings = {
  customMappings: Record<string, string | null>;
  surfacedDestinations: Record<string, string>;
};

interface UpsertResult {
  recordId: string;
  status: UpsertStatus;
}

type SignifyConfigurationData = {
  sessionId?: string | null;
  commercialCode?: string | null;
  complete?: boolean | null;
  variableAssignments?: string | null;
};

type SignifyProductData = ProductAndPriceAndAvailabilityResponse & {
  configuration?: SignifyConfigurationData;
};

/**
 * Hook for creating or updating LineItem entity records from Signify product data
 * Checks for existing records by manufacturersSourceId and manufacturersProductId, and then upserts accordingly
 * 
 * @returns Object with createLineItemRecord function that returns upsert status
 */
export const useCreateLineItemFromSignify = () => {
    const { add } = useAdd();
    const { set } = useSet();
    const { get } = useGet();
    const { user } = useContext(UserContext);
    const { fieldTypes } = useContext(FieldTypeContext);
    const { findByProductId } = useFindLineItemByProductId();
    const { mappings: signifyMappings } = useSignifyMappings();

    /**
     * Creates or updates a LineItem entity record from Signify product and price data
     * Checks for existing record by manufacturersProductId and performs upsert operation
     * 
     * @param productData - Combined product and price data from Signify API
     * @param sessionMappings - The session's overrides and the destinations they replace
     * @returns Promise resolving to upsert result with record ID and status
     */
    const buildLineItemFields = useCallback(async (
        productData: SignifyProductData,
        sessionMappings?: SignifySessionMappings,
        options?: { resolveEntityReferences?: boolean }
    ): Promise<{ fields: Fields; recordName: string; entityReferences: EntityRecord['entityReferences'] }> => {
        const resolveEntityReferences = options?.resolveEntityReferences ?? true;
        const fields: Fields = {};

        const writeField = (fieldId: string, rawValue: unknown) => {
            const value = coerceSignifyFieldValue({
                value: rawValue,
                fieldDataTypeId: fieldTypes[fieldId]?.fieldDataTypeId
            });
            if (value === undefined) return;
            fields[fieldId] = {
                value,
                meta: {
                    agentType: AgentType.API,
                    lastModified: serverTimestamp()
                }
            };
        };

        const response = indexSignifyProductPrices(productData);
        const fieldMappings = signifyLineItemFieldMappings(signifyMappings.fieldMappings);
        const resolved = resolveSignifyFieldValues({ fieldMappings, response });

        const requiredProductIdSource = Object.keys(REQUIRED_PRODUCT_ID_MAPPING)[0];
        const sessionOverrides = Object.entries(sessionMappings?.customMappings ?? {})
            .filter(([sourcePath]) => sourcePath !== requiredProductIdSource);
        const overriddenSources = new Set(sessionOverrides.map(([sourcePath]) => sourcePath));
        const surfacedDestinations = sessionMappings?.surfacedDestinations ?? {};

        const resolvedRecordName = resolveSignifyRecordName({ fieldMappings, response });
        if (resolvedRecordName.status === 'noValue') {
            throw new Error(
                'Unable to determine record name - no rule for the Record Name destination produced a value from this Signify product'
            );
        }
        if (resolvedRecordName.status === 'notText') {
            throw new Error(
                `Unable to determine record name - Rule ${resolvedRecordName.rulePosition} of the `
                + `Record Name destination produced ${resolvedRecordName.valueSummary}, `
                + 'which is blank or not text'
            );
        }
        const recordName = resolvedRecordName.name;

        Object.entries(resolved).forEach(([fieldId, field]) => {
            if (fieldId === SIGNIFY_RECORD_NAME_DESTINATION) return;
            const replacedBySession = field.source.type === 'path'
                && overriddenSources.has(field.source.path)
                && surfacedDestinations[field.source.path] === fieldId;
            if (replacedBySession) return;
            writeField(fieldId, field.value);
        });

        sessionOverrides.forEach(([sourcePath, fieldId]) => {
            if (!fieldId) return;
            const rawValue = getNestedValue(response, sourcePath);
            if (rawValue === null || rawValue === undefined || rawValue === '') return;
            writeField(fieldId, rawValue);
        });

        fields['manufacturersSourceId'] = {
            value: REQUIRED_SOURCE_ID_MAPPING,
            meta: {
                agentType: AgentType.API,
                lastModified: serverTimestamp()
            }
        };

        const brandCode = productData.product?.brand?.code;

        const entityReferences: EntityRecord['entityReferences'] = {};
        if (resolveEntityReferences) {
            const manufacturerRecordId = (() => {
                if (!brandCode) return null;
                const recordId = signifyMappings.manufacturerMappings[brandCode.toUpperCase().trim()];
                if (!recordId) {
                    console.warn(`[Signify] No manufacturer mapping found for brand code: "${brandCode}"`);
                    return null;
                }
                return recordId;
            })();

            if (manufacturerRecordId) {
                try {
                    const manufacturerRecord = await get<EntityRecord>(
                        'entities/Manufacturers/records',
                        manufacturerRecordId
                    );

                    if (manufacturerRecord) {
                        entityReferences['Manufacturers'] = {
                            entityRecordId: manufacturerRecordId,
                            entityRecordName: manufacturerRecord.name,
                            meta: {
                                agentType: AgentType.API,
                                lastModified: serverTimestamp()
                            }
                        };

                        const parentCompany = manufacturerRecord.fields?.parentCompany?.value;
                        if (parentCompany) {
                            fields['parentCompany'] = {
                                value: parentCompany,
                                meta: {
                                    agentType: AgentType.API,
                                    lastModified: serverTimestamp()
                                }
                            };
                        }
                    } else {
                        console.warn(
                            `[Signify] Manufacturer record not found for brand code "${brandCode}" ` +
                            `(recordId: ${manufacturerRecordId}). Entity reference not created.`
                        );
                    }
                } catch (error) {
                    console.warn(
                        `[Signify] Failed to validate/fetch manufacturer for brand code "${brandCode}" ` +
                        `(recordId: ${manufacturerRecordId}):`, error
                    );
                }
            }
        }

        return { fields, recordName, entityReferences };
    }, [get, fieldTypes, signifyMappings]);

    const createLineItemRecord = useCallback(async (
        productData: SignifyProductData,
        sessionMappings?: SignifySessionMappings
    ): Promise<UpsertResult> => {
        const { fields, recordName, entityReferences } = await buildLineItemFields(productData, sessionMappings);

        const productCode = fields.manufacturersProductId?.value as string;
        if (!productCode) {
            throw new Error('manufacturersProductId field is required but was not found in mapped fields');
        }

        const { record: existingRecord, hasDuplicates } = await findByProductId(productCode);

        if (hasDuplicates) {
            return {
                recordId: existingRecord!.docId!,
                status: 'duplicate'
            };
        }

        if (existingRecord) {
            const fieldsChanged = Object.keys(fields).some(fieldId => {
                const newValue = fields[fieldId]?.value;
                const existingValue = existingRecord.fields?.[fieldId]?.value;

                return JSON.stringify(newValue) !== JSON.stringify(existingValue);
            });

            const nameChanged = recordName !== existingRecord.name;

            if (!fieldsChanged && !nameChanged) {
                return {
                    recordId: existingRecord.docId!,
                    status: 'unchanged'
                };
            }

            const updatedRecord: Partial<EntityRecord> = {
                name: recordName,
                fields,
                entityReferences,
                meta: {
                    ...existingRecord.meta,
                    userId: user?.uid,
                    agentType: AgentType.API,
                    lastModified: serverTimestamp(),
                    searchNameLowercase: recordName.toLowerCase()
                }
            };

            await set('entities/Products/records', existingRecord.docId!, updatedRecord, AgentType.API);

            return {
                recordId: existingRecord.docId!,
                status: 'updated'
            };
        }

        const newRecord: Omit<EntityRecord, 'docId'> = {
            name: recordName,
            fields,
            entityReferences,
            status: RecordStatus.Active,
            meta: {
                userId: user?.uid,
                agentType: AgentType.API,
                created: serverTimestamp(),
                lastModified: serverTimestamp(),
                searchNameLowercase: recordName.toLowerCase()
            }
        };

        const recordId = await add('entities/Products/records', newRecord, AgentType.API);

        return {
            recordId,
            status: 'created'
        };
    }, [add, buildLineItemFields, set, user?.uid, findByProductId]);

    return { createLineItemRecord, buildLineItemFields };
};

